/* ozzi-popup.js — capture popup engine.
 *
 * Replaces Alia. Step 1 and 2 are a 1:1 reproduction of the live "Mystery
 * Discount" flow; step 3 (SMS) is wired but its copy comes from config so the
 * legal disclosure is never hard-coded in two places.
 *
 * Everything is namespaced oz-pop-* and scoped under #ozzi-popup. Config comes
 * from window.OZZI_POPUP, rendered by snippets/ozzi-popup.liquid.
 *
 * Deliberate properties:
 *  - No render-blocking work. Nothing runs until the page is interactive.
 *  - Suppression is evaluated before display and every decision is logged with
 *    a reason, so a drop in volume is always explainable.
 *  - The capture POST is fire-and-forget; the UI never waits on the network.
 */
(function () {
  "use strict";

  var CFG = window.OZZI_POPUP;
  if (!CFG) return;

  // ONE campaign runs per page view, chosen here. Most specific wins: a
  // campaign that names this path beats a site-wide one, so a product popup
  // can sit beside the site-wide popup without either having to list the
  // other's pages by hand.
  //
  // Running two at once was never on the table. Two modals racing for the same
  // visitor is the thing every popup vendor gets wrong, and the session and
  // capture caps below are all keyed to "the popup", singular.
  //
  // `campaign` (singular) is still honoured so nothing breaks if only one is
  // configured.
  function pickCampaign() {
    var all = CFG.campaigns && CFG.campaigns.length
      ? CFG.campaigns
      : (CFG.campaign ? [CFG.campaign] : []);
    var path = location.pathname;
    var fallback = null;

    for (var i = 0; i < all.length; i++) {
      var c = all[i];
      if (!c) continue;
      // A campaign that excludes this page is out of the running entirely, so
      // the next one down gets its turn rather than the page going quiet.
      if (c.excludePaths && c.excludePaths.length && matchesPath(path, c.excludePaths)) continue;
      // A page that sells two offers at one address (the capsule product page
      // and its ?view= test versions) is told apart by its query string.
      if (c.excludeQuery && matchesQuery(path, c.excludeQuery)) continue;
      if (c.includeQuery && matchesQuery(path, c.includeQuery)) return c;
      if (c.includePaths && c.includePaths.length) {
        if (matchesPath(path, c.includePaths)) return c;   // targeted: wins now
      } else if (!fallback) {
        fallback = c;                                      // site-wide: holds
      }
    }
    return fallback;
  }

  var C = pickCampaign();
  if (!C) return;
  var STORE_KEY = "ozzi_pop_v1";
  var VISITOR_KEY = "ozzi_pop_vid";
  var DAY = 86400000;

  // ---------------------------------------------------------------- storage

  function readStore() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function writeStore(s) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function visitorId() {
    try {
      var v = localStorage.getItem(VISITOR_KEY);
      if (v) return v;
      v = "v" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(VISITOR_KEY, v);
      return v;
    } catch (e) {
      return "v-anon-" + Math.random().toString(36).slice(2, 10);
    }
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Config numbers must not go through `||`. Every meaningful setting here has a
  // legitimate zero — a 0-day cooldown, a 0-weight variant that is switched off,
  // a dismissal limit of 0 — and `0 || fallback` silently swaps the value for
  // the default, which looks like the setting being ignored.
  function num(value, fallback) {
    return typeof value === "number" && isFinite(value) ? value : fallback;
  }

  var VID = visitorId();
  var store = readStore();

  // ---------------------------------------------------------------- variant
  // Deterministic bucketing: same visitor, same campaign, same variant, on every
  // page and every visit. No flicker and no server round trip.

  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    return h >>> 0;
  }

  function pickVariant() {
    var variants = C.variants && C.variants.length ? C.variants : [{ id: "control", weight: 1 }];
    store.variants = store.variants || {};
    var stuck = store.variants[C.id];
    if (stuck) {
      for (var i = 0; i < variants.length; i++) if (variants[i].id === stuck) return variants[i];
    }
    var total = 0, j;
    for (j = 0; j < variants.length; j++) total += num(variants[j].weight, 1);
    var point = (hash(VID + ":" + C.id) % 10000) / 10000 * total;
    var acc = 0, chosen = variants[0];
    for (j = 0; j < variants.length; j++) {
      acc += num(variants[j].weight, 1);
      if (point < acc) { chosen = variants[j]; break; }
    }
    store.variants[C.id] = chosen.id;
    writeStore(store);
    return chosen;
  }

  var VARIANT = pickVariant();
  // Variant content overlays the campaign's base content.
  var COPY = Object.assign({}, C.content, VARIANT.content || {});

  // ---------------------------------------------------------------- analytics

  function emit(event, extra) {
    var payload = Object.assign({
      event: event,
      campaign_id: C.id,
      variant_id: VARIANT.id,
      page_path: location.pathname,
      trigger_type: C.trigger && C.trigger.type,
      visitor_id: VID
    }, extra || {});

    // GA4 through the existing GTM container.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}

    // Durable copy for the CRO rollups. Beacon so it survives unload.
    if (!CFG.endpoints || !CFG.endpoints.event) return;
    try {
      var body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CFG.endpoints.event, new Blob([body], { type: "application/json" }));
      } else {
        fetch(CFG.endpoints.event, {
          method: "POST", keepalive: true,
          headers: { "Content-Type": "application/json" }, body: body
        }).catch(function () {});
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------- suppression
  // Global guardrails no campaign can override, then per-campaign frequency.
  // Returns a reason string when blocked, or null when clear.

  function suppressionReason() {
    var path = location.pathname;

    // A campaign can name the pages it owns. Until the whole site is cut over
    // from Alia this is what keeps the two from running on the same page.
    // A page the campaign won by its query string (includeQuery) is targeted
    // too: without that exception the popup was picked there and then never shown.
    if (C.includePaths && C.includePaths.length && !matchesPath(path, C.includePaths) &&
        !(C.includeQuery && matchesQuery(path, C.includeQuery))) {
      return "path_not_targeted";
    }

    // Pages the campaign owns but stays silent on. Unlike excludePaths, the
    // campaign still wins the page in pickCampaign(), so the site-wide popup
    // does not step in. For a product on a waitlist: the page has its own
    // signup, and a first-order code for something that cannot be bought is a
    // promise the page cannot keep.
    if (C.quietPaths && C.quietPaths.length && matchesPath(path, C.quietPaths)) {
      return "path_quiet";
    }

    // Pages the campaign is kept off entirely. /account is in the default list
    // below rather than hardcoded here, so it stays visible and editable next to
    // the rest of the targeting.
    if (C.excludePaths && C.excludePaths.length && matchesPath(path, C.excludePaths)) {
      return "path_excluded";
    }

    if (/^\/(cart|checkout)/.test(path) || /^\/\d+\/checkouts/.test(path)) return "checkout_path";
    if (CFG.customerLoggedIn && C.skipLoggedIn !== false) return "known_customer";

    // Existing customers do not get a first-order offer. Two signals, because
    // neither covers the other:
    //   - customerHasOrders is Liquid's own order count, which is authoritative
    //     but only exists while they are logged in.
    //   - ozzi_bought is our first-party cookie, which survives logout and
    //     covers guest checkout. It is set by the order status page snippet
    //     (see snippets/ozzi-popup.liquid); without that snippet installed this
    //     half is simply inert.
    if (C.excludePastCustomers !== false) {
      if (CFG.customerHasOrders) return "past_customer";
      if (cookie("ozzi_bought")) return "past_customer";
    }
    if (document.getElementById("ozzi-builder") && C.skipBuilderPages) return "builder_page";

    // A visitor pinned to the free-sample or affiliate arm must never see a
    // discount popup — this replaces the hide-Alia hack in ozzi-offer-cohort.
    var offer = cookie("ozzi_offer");
    if (offer && (C.excludeCohorts || []).indexOf(offer) !== -1) return "cohort_" + offer;

    if (store.session === sessionId()) return "already_this_session";

    var caps = store.captured || {};
    if (caps.email && caps.sms) return "already_subscribed_both";
    // stopAfterEmail: once someone has given an email, never ask again, even
    // if they skipped the phone step (Mars Men's "new visitors only" rule).
    if (caps.email && (!C.asksSms || C.stopAfterEmail)) return "already_subscribed_email";

    if (num(store.dismissCount, 0) >= num(C.globalDismissLimit, 2)) {
      var until = num(store.lastDismissAt, 0) + num(C.globalDismissDays, 90) * DAY;
      if (Date.now() < until) return "dismiss_limit";
    }

    var seen = (store.seen || {})[C.id];
    if (seen && Date.now() - seen < num(C.cooldownDays, 30) * DAY) return "campaign_cooldown";

    return null;
  }

  // Paths are matched literally, with an optional trailing "*" for a prefix.
  // No regex from config: a stray character in a campaign entry should narrow a
  // launch, never accidentally widen it to the whole site.
  // includeQuery: { paths: [...], params: [["view", "x"], ["oz_ab", "single"]] }.
  // Matches when the path is one of `paths` (any path if empty) and the URL
  // carries at least one of the key=value pairs. excludeQuery takes the same
  // shape and drops the campaign from pickCampaign() on a match.
  function matchesQuery(path, rules) {
    if (!rules || !rules.params || !rules.params.length) return false;
    if (rules.paths && rules.paths.length && !matchesPath(path, rules.paths)) return false;
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { return false; }
    for (var i = 0; i < rules.params.length; i++) {
      var pair = rules.params[i] || [];
      if (pair.length === 2 && q.get(pair[0]) === String(pair[1])) return true;
    }
    return false;
  }

  function matchesPath(path, patterns) {
    var clean = path.replace(/\/+$/, "") || "/";
    for (var i = 0; i < patterns.length; i++) {
      var p = String(patterns[i]).replace(/\/+$/, "") || "/";
      if (p.charAt(p.length - 1) === "*") {
        if (clean.indexOf(p.slice(0, -1)) === 0) return true;
      } else if (clean === p) {
        return true;
      }
    }
    return false;
  }

  // Hide Alia on the pages this campaign has taken over, so a visitor never gets
  // two popups during the cutover. Same conservative hyphen-scoped selector as
  // ozzi-offer-cohort, so it can only ever touch Alia's own nodes.
  function suppressAlia() {
    var sel = '[id^="alia-"],[id*="alia-widget" i],[class^="alia-"],[class*=" alia-" i],[data-alia],iframe[src*="alia" i]';
    var kill = function () {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el === document.body || el === document.documentElement) return;
        el.style.setProperty("display", "none", "important");
      });
    };
    kill();
    // Alia mounts late and re-renders, so watch briefly rather than firing once.
    try {
      var mo = new MutationObserver(kill);
      mo.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 15000);
    } catch (e) {}
  }

  function sessionId() {
    try {
      var s = sessionStorage.getItem("ozzi_pop_sid");
      if (!s) {
        s = "s" + Date.now().toString(36);
        sessionStorage.setItem("ozzi_pop_sid", s);
      }
      return s;
    } catch (e) { return "s-none"; }
  }

  // ---------------------------------------------------------------- state

  var el = null;
  var opened = false;
  var answer = null;
  var email = null;
  // Seeded with the shared fallback so the redeem link always carries a working
  // code; a minted per-visitor code replaces it the moment /api/capture answers.
  // Some campaigns hand out no code at all (the Ozzi Roll gives a roll on the
  // thank you page instead). For those, nothing is ever applied to the session
  // and a code arriving from the API is ignored, or the popup would quietly
  // put a 15% code on a visitor it just told the order might be free.
  var usesCode = !COPY.noCode;
  var reward = usesCode ? (COPY.fallbackCode || null) : null;

  // ---------------------------------------------------------------- markup

  // Six pips. Drawn, not an image: it has to sit in the mark slot at two sizes
  // and follow the popup's palette.
  function dieMark() {
    var pips = [[26,24],[26,42],[26,60],[58,24],[58,42],[58,60]], c = "";
    for (var i = 0; i < pips.length; i++) {
      c += '<circle cx="' + pips[i][0] + '" cy="' + pips[i][1] + '" r="6.5" fill="var(--pop-mint)"/>';
    }
    return '<svg class="oz-pop-logo oz-pop-die" viewBox="0 0 84 84" role="img" aria-label="A die showing six">' +
             '<rect x="2" y="2" width="80" height="80" rx="14" fill="var(--pop-green)"/>' + c +
           '</svg>';
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // The live popup shuffles the persona options and remembers the order for the
  // visitor rather than reshuffling on every page. Same behaviour here, done
  // deterministically off the visitor id so it survives a reload without needing
  // to be stored, and so the recorded order is reproducible from the event log.
  function orderedOptions() {
    var opts = (COPY.options || []).slice();
    if (COPY.shuffleOptions === false) return opts;

    // Fisher-Yates driven by a seeded PRNG. Position bias is real on a
    // six-option list, and a fixed order would quietly bias every persona
    // segment toward whatever sits at the top.
    var seed = hash(VID + ":" + C.id + ":options");
    function rnd() {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    }
    for (var i = opts.length - 1; i > 0; i--) {
      var j = Math.floor(rnd() * (i + 1));
      var t = opts[i]; opts[i] = opts[j]; opts[j] = t;
    }
    return opts;
  }

  var OPTION_ORDER = orderedOptions();

  function optionButtons() {
    return OPTION_ORDER.map(function (o, i) {
      // { label, close: true } is a decline, not an answer: it closes the popup
      // like the X does and records nothing.
      if (o.close) {
        return '<button type="button" class="oz-pop-skip" data-oz-close>' + esc(o.label) + "</button>";
      }
      return '<button type="button" class="oz-pop-btn" data-oz-answer="' + esc(o.answer) +
             '" data-oz-persona="' + esc(o.persona) + '" data-oz-index="' + i + '">' +
             esc(o.label) + "</button>";
    }).join("");
  }

  // Mobile is a full-bleed background photo with the content laid over it;
  // desktop is a three-image collage beside the content and no background photo
  // at all. Both are always in the DOM and CSS picks which applies, so there is
  // no layout shift on resize and no width-sniffing in JS.
  function backgroundMarkup() {
    var art = COPY.art || {};
    if (!art.mobileBackground) return "";
    return '<div class="oz-pop-bg" style="background-image:url(' +
           esc(art.mobileBackground).replace(/[()]/g, "") + ')"></div>';
  }

  function artMarkup() {
    var art = COPY.art || {};
    var out = "";

    if (art.collage && art.collage.length) {
      out += '<div class="oz-pop-collage">' + art.collage.map(function (p) {
        var style = "left:" + esc(p.left) + ";top:" + esc(p.top) +
                    ";width:" + esc(p.width) + ";aspect-ratio:" + esc(p.ratio) +
                    ";height:auto;z-index:" + esc(p.z || 1);
        // A product render shot on pure white sits on a near-white panel and
        // reads as a visible rectangle. multiply drops its background out
        // instead of us needing a cut-out PNG of every product.
        if (p.blend) style += ";mix-blend-mode:" + esc(p.blend);
        return '<img src="' + esc(p.src) + '" alt="" loading="lazy" decoding="async" style="' + style + '">';
      }).join("") + "</div>";
    }

    return out ? '<div class="oz-pop-art">' + out + "</div>" : "";
  }

  function build() {
    var wrap = document.createElement("div");
    wrap.id = "ozzi-popup";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-label", COPY.ariaLabel || "Sign up offer");
    wrap.setAttribute("data-open", "false");

    var art = artMarkup();

    var logo = COPY.mark === "die"
      ? dieMark()
      : COPY.logo
        ? '<img class="oz-pop-logo" src="' + esc(COPY.logo) + '" alt="' + esc(CFG.shopName || "Ozzi") + '">'
        : "";

    wrap.innerHTML =
      '<div class="oz-pop-overlay" data-oz-overlay>' +
        backgroundMarkup() +
        '<button type="button" class="oz-pop-close" data-oz-close aria-label="Close">' +
          '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
            '<path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
        '<div class="oz-pop-stage">' +
          '<div class="oz-pop-content">' +
            logo +

            /* step 1 — persona question */
            '<div class="oz-pop-step" data-oz-step="question">' +
              '<h2 class="oz-pop-h">' + esc(COPY.questionHeading) + '</h2>' +
              (COPY.questionPrompt ? '<p class="oz-pop-prompt">' + esc(COPY.questionPrompt) + '</p>' : "") +
              '<div class="oz-pop-stack">' + optionButtons() + '</div>' +
            '</div>' +

            /* step 2 — email */
            '<div class="oz-pop-step" data-oz-step="email">' +
              '<h2 class="oz-pop-h">' + esc(COPY.emailHeading) + '</h2>' +
              (COPY.emailSub ? '<p class="oz-pop-sub">' + esc(COPY.emailSub) + '</p>' : "") +
              '<form class="oz-pop-stack" data-oz-form="email" novalidate>' +
                '<input class="oz-pop-input" type="email" name="email" autocomplete="email" ' +
                  'inputmode="email" placeholder="' + esc(COPY.emailPlaceholder) + '" ' +
                  'aria-label="' + esc(COPY.emailPlaceholder) + '" required>' +
                '<p class="oz-pop-err" data-oz-err="email" role="alert"></p>' +
                '<button type="submit" class="oz-pop-btn">' + esc(COPY.emailCta) + '</button>' +
                (COPY.emailFineHtml ? '<p class="oz-pop-fine">' + COPY.emailFineHtml + '</p>' : "") +
              '</form>' +
            '</div>' +

            /* step 3 — SMS. Copy and disclosure come from config; nothing about
               the consent language is authored here. */
            '<div class="oz-pop-step" data-oz-step="sms">' +
              '<h2 class="oz-pop-h">' + esc(COPY.smsHeading) + '</h2>' +
              (COPY.smsSub ? '<p class="oz-pop-sub">' + esc(COPY.smsSub) + '</p>' : "") +
              '<form class="oz-pop-stack" data-oz-form="sms" novalidate>' +
                /* Disclosure sits directly above the phone field and the submit
                   control, visible without scrolling or expanding. Rendered as
                   trusted HTML from config so the policy links stay clickable —
                   the consent language is never authored here. */
                '<div class="oz-pop-consent">' + (COPY.smsConsentHtml || "") + '</div>' +
                '<div class="oz-pop-phone">' +
                  '<span class="oz-pop-flag" aria-hidden="true">🇺🇸<span>+1</span></span>' +
                  '<input class="oz-pop-input" type="tel" name="phone" autocomplete="tel-national" ' +
                    'inputmode="tel" placeholder="' + esc(COPY.smsPlaceholder) + '" ' +
                    'aria-label="' + esc(COPY.smsPlaceholder) + ' (United States)" required>' +
                '</div>' +
                '<p class="oz-pop-err" data-oz-err="sms" role="alert"></p>' +
                '<button type="submit" class="oz-pop-btn">' + esc(COPY.smsCta) + '</button>' +
                (COPY.smsSkip ? '<button type="button" class="oz-pop-skip" data-oz-skip>' + esc(COPY.smsSkip) + '</button>' : "") +
              '</form>' +
            '</div>' +

            /* step 4 — reward */
            '<div class="oz-pop-step" data-oz-step="reward">' +
              '<h2 class="oz-pop-h">' + esc(COPY.rewardHeading) +
                (COPY.rewardHeadingSub
                  ? '<span class="oz-pop-hsub">' + esc(COPY.rewardHeadingSub) + '</span>'
                  : "") +
              '</h2>' +
              (COPY.rewardBodyHtml ? '<div class="oz-pop-body">' + COPY.rewardBodyHtml + '</div>' : "") +
              '<div class="oz-pop-stack">' +
                (usesCode
                  ? '<button type="button" class="oz-pop-code" data-oz-code ' +
                      'aria-label="Copy discount code">' +
                      '<span data-oz-code-text>' + esc(COPY.fallbackCode || "") + '</span>' +
                      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
                        '<rect x="5.5" y="5.5" width="9" height="9" rx="1.5" stroke="currentColor"/>' +
                        '<path d="M10.5 3.5v-1a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h1" stroke="currentColor"/>' +
                      '</svg>' +
                    '</button>'
                  : "") +
                '<button type="button" class="oz-pop-btn" data-oz-redeem>' + esc(COPY.rewardCta) + '</button>' +
              '</div>' +
              /* Terms sit below the button, where the offer is already understood.
                 Rendered as trusted HTML from config so the rules link stays
                 clickable; no legal wording is authored here. */
              (COPY.rewardSubHtml ? '<p class="oz-pop-rsub">' + COPY.rewardSubHtml + '</p>' : "") +
              (COPY.rewardFineHtml ? '<p class="oz-pop-fine">' + COPY.rewardFineHtml + '</p>' : "") +
            '</div>' +

          '</div>' +
          art +
        '</div>' +
      '</div>';

    document.body.appendChild(wrap);
    return wrap;
  }

  // ---------------------------------------------------------------- steps

  // Put the reward code on the visitor's session the moment they have earned it,
  // so it is already applied if they carry on browsing and check out normally
  // rather than clicking through the reward button. Hitting /discount/{code} is
  // what sets Shopify's discount cookie; ?redirect=/cart.js keeps the response
  // to a few bytes of JSON instead of rendering a whole page.
  //
  // Fire and forget: this is a convenience on top of the button, never a
  // precondition for it, so a failure here must stay silent.
  var appliedCode = null;

  function applyReward() {
    if (!reward || reward === appliedCode) return;
    appliedCode = reward;
    try {
      fetch("/discount/" + encodeURIComponent(reward) + "?redirect=/cart.js", {
        credentials: "same-origin"
      })
        .then(function () { emit("popup_code_applied", { code: reward }); })
        .catch(function () {});
    } catch (e) {}
  }

  function show(step) {
    var steps = el.querySelectorAll("[data-oz-step]");
    for (var i = 0; i < steps.length; i++) {
      steps[i].setAttribute("data-active", steps[i].getAttribute("data-oz-step") === step ? "true" : "false");
    }
    var focusable = el.querySelector('[data-oz-step][data-active="true"] input, [data-oz-step][data-active="true"] button');
    if (focusable && step !== "question") {
      // Don't steal focus on open; only on advance.
      if (opened) setTimeout(function () { focusable.focus(); }, 60);
    }
    if (step === "reward") applyReward();
    emit("popup_step", { step: step, persona: answer && answer.persona });
  }

  function open() {
    if (opened) return;
    opened = true;
    el.setAttribute("data-open", "true");
    document.documentElement.classList.add("oz-pop-locked");
    document.body.classList.add("oz-pop-locked");

    store.seen = store.seen || {};
    store.seen[C.id] = Date.now();
    store.session = sessionId();
    writeStore(store);

    // Record the order the options were actually shown in. Without it a persona
    // split can't be read honestly, because position moves the numbers.
    emit("popup_impression", {
      option_order: OPTION_ORDER.map(function (o) { return o.persona; }).join("|")
    });
  }

  function close(reason) {
    if (!opened) return;
    opened = false;
    el.setAttribute("data-open", "false");
    document.documentElement.classList.remove("oz-pop-locked");
    document.body.classList.remove("oz-pop-locked");

    if (reason === "dismissed") {
      store.dismissCount = (store.dismissCount || 0) + 1;
      store.lastDismissAt = Date.now();
      writeStore(store);
      emit("popup_dismissed", { persona: answer && answer.persona });
    }
  }

  // ---------------------------------------------------------------- capture

  function post(path, body) {
    if (!CFG.endpoints || !CFG.endpoints.capture) return Promise.resolve(null);
    return fetch(CFG.endpoints.capture + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function baseBody() {
    return {
      campaign_id: C.id,
      variant_id: VARIANT.id,
      popup_name: C.popupName,
      page_url: location.href,
      page_path: location.pathname,
      visitor_id: VID,
      answer: answer && answer.answer,
      persona: answer && answer.persona,
      answer_property: COPY.answerProperty,
      // Which profile property the persona lands in. Two campaigns answering
      // two different questions must not write to the same property, or one
      // campaign's answers quietly satisfy the other's segment conditions.
      persona_property: COPY.personaProperty,
      offer_cohort: cookie("ozzi_offer") || null
    };
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function submitEmail(form) {
    var input = form.querySelector('input[name="email"]');
    var err = form.querySelector('[data-oz-err="email"]');
    var value = (input.value || "").trim();

    if (!EMAIL_RE.test(value)) {
      input.setAttribute("aria-invalid", "true");
      err.textContent = COPY.emailError || "Enter a valid email address.";
      input.focus();
      return;
    }
    input.removeAttribute("aria-invalid");
    err.textContent = "";
    email = value;

    var btn = form.querySelector("button[type=submit]");
    btn.disabled = true;

    store.captured = store.captured || {};
    store.captured.email = true;
    writeStore(store);

    emit("popup_email_captured", { persona: answer && answer.persona });
    writeCartAttributes();

    // Signup is complete at the email: the SMS step is a bonus ask, and most
    // people never answer it. Apply the code here rather than waiting for the
    // reward step, or an email-only signup walks away with nothing on their
    // session. This puts the shared fallback on now; the .then below swaps in
    // the minted code as soon as it lands.
    applyReward();

    var body = Object.assign(baseBody(), { email: email });

    post("/email", body).then(function (res) {
      btn.disabled = false;
      if (res && res.discount_code && usesCode) {
        reward = res.discount_code;
        var code = el.querySelector("[data-oz-code-text]");
        if (code) code.textContent = reward;
        // The minted code can land after the reward step is already on screen
        // (the flow does not wait on the network), so apply it here too. It
        // replaces the shared fallback that applyReward may have set.
        applyReward();
      }
      show(C.asksSms ? "sms" : "reward");
    });

    // Don't make the visitor wait on the network for the next step.
    setTimeout(function () {
      if (el.querySelector('[data-oz-step="email"][data-active="true"]')) {
        btn.disabled = false;
        show(C.asksSms ? "sms" : "reward");
      }
    }, 1200);
  }

  function submitSms(form) {
    var input = form.querySelector('input[name="phone"]');
    var err = form.querySelector('[data-oz-err="sms"]');
    var digits = (input.value || "").replace(/[^\d]/g, "");

    // US/CA only, matching where the sending number is registered. 10 digits, or
    // 11 beginning with a country code of 1.
    if (digits.length === 11 && digits.charAt(0) === "1") digits = digits.slice(1);
    if (digits.length !== 10) {
      input.setAttribute("aria-invalid", "true");
      err.textContent = COPY.smsError || "Enter a valid 10-digit mobile number.";
      input.focus();
      return;
    }
    input.removeAttribute("aria-invalid");
    err.textContent = "";

    var btn = form.querySelector("button[type=submit]");
    btn.disabled = true;

    store.captured = store.captured || {};
    store.captured.sms = true;
    writeStore(store);

    emit("popup_sms_captured", { persona: answer && answer.persona });

    // The consent record is written server-side. The client sends the exact
    // disclosure it displayed plus its version, so proof of consent reflects
    // what this visitor actually saw rather than whatever config says today.
    var body = Object.assign(baseBody(), {
      email: email,
      phone: "+1" + digits,
      sms_consent: true,
      consent_version: COPY.smsConsentVersion,
      consent_text: consentPlainText()
    });

    post("/sms", body).then(function () { btn.disabled = false; });
    show("reward");
  }

  function consentPlainText() {
    var node = el.querySelector(".oz-pop-consent");
    return node ? node.textContent.replace(/\s+/g, " ").trim() : "";
  }

  // Campaign and variant ride the cart into the order, so revenue attribution
  // survives without depending on cookie lifetime or the same device.
  function writeCartAttributes() {
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            _ozzi_popup: C.id,
            _ozzi_variant: VARIANT.id,
            _ozzi_persona: (answer && answer.persona) || ""
          }
        })
      }).catch(function () {});
    } catch (e) {}
  }

  // ---------------------------------------------------------------- wiring

  function wire() {
    el.addEventListener("click", function (e) {
      var t = e.target;

      var opt = t.closest ? t.closest("[data-oz-answer]") : null;
      if (opt) {
        answer = { answer: opt.getAttribute("data-oz-answer"), persona: opt.getAttribute("data-oz-persona") || null };
        // Record the persona immediately. A visitor who answers and then bails
        // still told us something, and Alia recorded it at this point too.
        post("/answer", baseBody());
        show("email");
        return;
      }

      if (t.closest && t.closest("[data-oz-close]")) { close("dismissed"); return; }
      if (t.closest && t.closest("[data-oz-skip]")) { show("reward"); return; }

      if (t.closest && t.closest("[data-oz-code]")) {
        var span = el.querySelector("[data-oz-code-text]");
        var value = span ? span.textContent.trim() : "";
        if (value && navigator.clipboard) {
          navigator.clipboard.writeText(value).then(function () {
            var prev = span.textContent;
            span.textContent = "Copied";
            setTimeout(function () { span.textContent = prev; }, 1400);
          }).catch(function () {});
        }
        emit("popup_code_copied", { code: value });
        return;
      }

      if (t.closest && t.closest("[data-oz-redeem]")) {
        emit("popup_reward_clicked", { code: reward });
        close("converted");
        if (COPY.rewardUrl) {
          location.href = reward
            ? COPY.rewardUrl.replace("{code}", encodeURIComponent(reward))
            : COPY.rewardUrl.replace(/\/discount\/\{code\}/, "");
        }
        return;
      }

      // Click on the overlay itself, outside the content, closes.
      if (t.hasAttribute && t.hasAttribute("data-oz-overlay")) close("dismissed");
    });

    el.addEventListener("submit", function (e) {
      var form = e.target;
      var kind = form.getAttribute && form.getAttribute("data-oz-form");
      if (!kind) return;
      e.preventDefault();
      if (kind === "email") submitEmail(form);
      if (kind === "sms") submitSms(form);
    });

    // Submit on Enter explicitly rather than relying on the browser's implicit
    // form submission. Implicit submission is subtly conditional (field count,
    // button type, IME composition state) and a visitor who types their email
    // and hits Enter must never land on a dead form.
    el.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" || e.isComposing) return;
      var input = e.target;
      if (!input || input.tagName !== "INPUT") return;
      var form = input.form;
      if (!form || !form.getAttribute("data-oz-form")) return;
      e.preventDefault();
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && opened) close("dismissed");
    });
  }

  // ---------------------------------------------------------------- triggers

  // Cart state is read twice on purpose. The Liquid count is correct at render
  // and costs nothing, but the page sits for the whole delay before we decide,
  // and Shopify can serve a cached page, so the live /cart.js is what actually
  // rules. A failed or slow request falls back to the render-time count rather
  // than suppressing the popup, because a network blip should not silently cost
  // us every capture on the page.
  function cartIsEmpty(done) {
    if (C.skipWhenCartHasItems === false) { done(true); return; }

    var settled = false;
    var atRender = num(CFG.cartItemCount, 0) === 0;
    var finish = function (empty) {
      if (settled) return;
      settled = true;
      done(empty);
    };

    var timer = setTimeout(function () { finish(atRender); }, 1500);

    try {
      fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          clearTimeout(timer);
          finish(cart ? num(cart.item_count, 0) === 0 : atRender);
        })
        .catch(function () { clearTimeout(timer); finish(atRender); });
    } catch (e) {
      clearTimeout(timer);
      finish(atRender);
    }
  }

  // "Actively shopping" is an interaction, not a URL. #ozzi-builder renders on
  // the homepage and the main PDP as well as the landers, so treating its mere
  // presence as "shopping" (skipBuilderPages) would take the popup off most of
  // the site's traffic. Watch for a real touch instead: a click or a change
  // inside the builder means they are configuring a box, and we stay out of it.
  //
  // Page-scoped on purpose, not session-scoped. Someone who fiddles with the
  // builder and then wanders off to the blog is a good target again; if they
  // actually committed, the empty-cart rule is what catches them.
  //
  // Delegated from document with capture, so it works whether the builder is in
  // the initial HTML or rendered later, and it never blocks the builder's own
  // handlers.
  var builderEngaged = false;

  function watchBuilder() {
    if (C.skipBuilderEngaged === false) return;
    var onInteract = function (e) {
      if (builderEngaged) return;
      var t = e.target;
      if (t && t.closest && t.closest("#ozzi-builder")) builderEngaged = true;
    };
    document.addEventListener("click", onInteract, true);
    document.addEventListener("change", onInteract, true);
  }

  // Country, resolved once per page and remembered for the session. The request
  // goes out at boot and has the whole trigger delay to land, so the decision at
  // fire time is almost always already made.
  //
  // Fails OPEN: if we cannot tell where someone is, showing them the popup is
  // the lesser mistake. Suppressing on every network hiccup would quietly cost
  // real US captures, and the offer itself is harmless outside the US — the
  // shipping and the SMS consent copy are what actually need to be US-only, and
  // both are enforced where they matter rather than here.
  var geoCache = null;

  function countryAllowed(done) {
    var allow = C.allowCountries;
    if (!allow || !allow.length) { done(true); return; }

    if (geoCache !== null) { done(allow.indexOf(geoCache) !== -1 || geoCache === "?"); return; }

    var stored = null;
    try { stored = sessionStorage.getItem("ozzi_pop_geo"); } catch (e) {}
    if (stored) {
      geoCache = stored;
      done(allow.indexOf(stored) !== -1 || stored === "?");
      return;
    }

    var settled = false;
    var finish = function (code) {
      if (settled) return;
      settled = true;
      geoCache = code;
      try { sessionStorage.setItem("ozzi_pop_geo", code); } catch (e) {}
      done(allow.indexOf(code) !== -1 || code === "?");
    };

    if (!CFG.endpoints || !CFG.endpoints.geo) { finish("?"); return; }
    var timer = setTimeout(function () { finish("?"); }, 2500);

    try {
      fetch(CFG.endpoints.geo, { credentials: "omit" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          clearTimeout(timer);
          finish((j && j.country) || "?");
        })
        .catch(function () { clearTimeout(timer); finish("?"); });
    } catch (e) {
      clearTimeout(timer);
      finish("?");
    }
  }

  function arm() {
    var trig = C.trigger || { type: "delay", seconds: 33 };
    var fired = false;

    // The delay is time on THIS page: the timer dies with the page load, and a
    // visitor who navigates away mid-countdown never gets it. pagehide covers
    // the case where the timer and the unload race.
    var left = false;
    window.addEventListener("pagehide", function () { left = true; });

    function fire(kind) {
      if (fired) return;
      fired = true;
      C.trigger = C.trigger || {};
      C.trigger.type = kind;

      if (left) { emit("popup_suppressed", { reason: "left_page" }); return; }
      if (builderEngaged) { emit("popup_suppressed", { reason: "builder_engaged" }); return; }

      countryAllowed(function (ok) {
        if (!ok) { emit("popup_suppressed", { reason: "country_" + (geoCache || "unknown") }); return; }
        cartIsEmpty(function (empty) {
          if (!empty) { emit("popup_suppressed", { reason: "cart_has_items" }); return; }
          if (left) { emit("popup_suppressed", { reason: "left_page" }); return; }
          // Re-checked after the async hops: the country and cart calls take a
          // moment, and that is long enough for someone to reach the builder.
          if (builderEngaged) { emit("popup_suppressed", { reason: "builder_engaged" }); return; }
          open();
        });
      });
    }

    if (trig.seconds) setTimeout(function () { fire("delay"); }, trig.seconds * 1000);

    if (trig.scrollPercent) {
      var onScroll = function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        if (h > 0 && window.scrollY / h * 100 >= trig.scrollPercent) {
          window.removeEventListener("scroll", onScroll);
          fire("scroll");
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    // Exit intent is the second path to the same popup, but it is not armed
    // immediately: someone who leaves in the first few seconds never engaged,
    // and interrupting that bounce buys a dismissal, not an email. The floor is
    // config, so it can be tuned without touching this file.
    if (trig.exitIntent) {
      var armedAt = Date.now() + num(trig.exitIntentAfterSeconds, 5) * 1000;
      document.addEventListener("mouseout", function (e) {
        if (Date.now() < armedAt) return;
        if (!e.relatedTarget && e.clientY <= 0) fire("exit_intent");
      });
    }
  }

  // ---------------------------------------------------------------- boot

  function boot() {
    var reason = suppressionReason();
    if (reason) { emit("popup_suppressed", { reason: reason }); return; }

    emit("popup_eligible");
    // Warm the country lookup now rather than at fire time, so the answer is
    // already in hand when the trigger fires and the popup never waits on it.
    countryAllowed(function () {});
    watchBuilder();
    if (C.suppressAlia) suppressAlia();
    el = build();
    wire();
    show("question");
    arm();

    // Manual trigger, for a "get my discount" link anywhere on the page.
    window.OzziPopup = {
      open: function () { open(); },
      close: function () { close("api"); },
      campaign: C.id,
      variant: VARIANT.id
    };
  }

  if (document.readyState === "complete") {
    setTimeout(boot, 0);
  } else {
    window.addEventListener("load", function () { setTimeout(boot, 0); });
  }
})();
